import React, { useState } from "react";
import { Popup, Input, Button, List, Icon } from "semantic-ui-react";

// Styles
import "react-lib/apps/HISV3/BIL/DropDownVoucherInput.scss";

interface DropDownVoucherInputProps {
  options: Record<string, any>[];
  value?: Record<string, any>;
  onChange?: (option: Record<string, any>) => void;
  title?: string;
  placeholder?: string;
}

const DropDownVoucherInput = ({
  options,
  value,
  onChange,
  placeholder = "พิมพ์ชื่อหรือ Code เพื่อค้นหา...",
}: DropDownVoucherInputProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const selectedOption = options.find((option) => option?.value === value?.value);

  const filteredOptions = options.filter((option) =>
    String(option?.text ?? "")
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );

  const handleApply = () => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      setError("กรุณาระบุชื่อหรือ Code ส่วนลด");
      return;
    }

    // ค้นหาจาก Code หรือชื่อ Voucher
    const matchedOptions = options.filter((option) => {
      const code = String(option?.code ?? "")
        .trim()
        .toLowerCase();
      const text = String(option?.text ?? "")
        .trim()
        .toLowerCase();

      return code.includes(keyword) || text.includes(keyword);
    });

    if (matchedOptions.length === 0) {
      setError("ไม่พบส่วนลด");
      return;
    }

    if (matchedOptions.length > 1) {
      setError("พบหลายรายการ กรุณาระบุชื่อหรือ Code ให้ชัดเจน");
      return;
    }

    onChange?.(matchedOptions[0]);
    setError("");
    setOpen(false);
    setSearch("");
  };

  const handleSelect = (option: Record<string, any>) => {
    onChange?.(option);
    setError("");
    setOpen(false);
    setSearch("");
  };

  return (
    <Popup
      open={open}
      on="click"
      position="bottom left"
      onClose={() => setOpen(false)}
      trigger={
        <Button
          fluid
          basic
          type="button"
          onClick={() => setOpen(!open)}
          className="search-dropdown-trigger"
        >
          <span style={{ fontSize: "14px" }}>
            {selectedOption?.text
              ? `${selectedOption.text} (-${selectedOption.discount_amount} ฿)`
              : "เลือก Voucher/สแกนโค้ด"}
          </span>

          <Icon
            name={open ? "chevron up" : "chevron down"}
            style={{ fontSize: "10px", marginLeft: "8px" }}
          />
        </Button>
      }
      content={
        <div className="search-dropdown-panel">
          {/* Header */}
          <div className="search-dropdown-header">
            <div>คูปองที่ใช้ได้กับบิลนี้</div>
            <div style={{ color: "var(--primary-theme-color)" }}>{`พบ ${options.length} ใบ`}</div>
          </div>

          {/* Options */}
          <div className="search-dropdown-options">
            {filteredOptions.length > 0 ? (
              <List selection verticalAlign="middle">
                {filteredOptions.map((option) => (
                  <List.Item
                    key={option.key}
                    active={option.value === value?.value}
                    onClick={() => handleSelect(option)}
                  >
                    <List.Content>
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <div>{option?.code}</div>
                          <div style={{ color: "var(--primary-theme-color)" }}>
                            {`- ${option?.discount_amount} ฿`}
                          </div>
                        </div>

                        <div>{`ส่วนลด${option?.template?.value_display}`}</div>
                      </div>
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            ) : (
              <div className="search-dropdown-empty">ไม่พบข้อมูล</div>
            )}
          </div>

          {/* Search footer */}
          <div className="search-dropdown-footer">
            <Input
              fluid
              icon="search"
              placeholder={placeholder}
              value={search}
              error={Boolean(error)}
              onChange={(_, data) => {
                setSearch(String(data.value ?? ""));
                setError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleApply();
                }
              }}
            />

            <Button primary type="button" onClick={handleApply}>
              ใช้
            </Button>

            {error && (
              <div
                role="alert"
                style={{
                  color: "red",
                  fontSize: "12px",
                  marginTop: "6px",
                  width: "100%",
                }}
              >
                {error}
              </div>
            )}
          </div>
        </div>
      }
      basic
      className="search-dropdown-popup"
    />
  );
};

export default DropDownVoucherInput;
